export type NodeType = 'AUTO_APPROVE' | 'PARALLEL' | 'HIERARCHY';
export type WorkflowStatus = 'DRAFT' | 'PUBLISHED';

export interface TriggerCondition {
  id: string;
  field: string;
  operator: string;
  value: number | string;
  currency?: string;
}

export interface Approver {
  id?: string;
  name: string;
  type: 'user' | 'role';
  email?: string;
  memberCount?: number;
}

export interface DecisionRule {
  type: 'ANY_ONE' | 'N_OF_M' | 'ALL';
  minApprovals?: number;
  totalApprovers?: number;
  description?: string;
}

export interface EscalationStep {
  id: string;
  name: string;
  email?: string;
}

export interface SLAConfig {
  reminderHours: number;
  maxEscalations: number;
  onTimeout: 'ESCALATE' | 'AUTO_APPROVE' | 'REJECT';
  escalationChain: EscalationStep[];
  worstCaseHours?: number;
}

export interface DelegationUser {
  id?: string;
  name: string;
  type: 'user' | 'role';
  memberCount?: number;
  description?: string;
}

export interface NodeMetadata {
  id: string;
  createdAt: string;
  createdBy: string;
  lastEdited: string;
  editCount: number;
  usedBy: string;
}

export interface WorkflowNode {
  id: string;
  type: NodeType;
  label: string;
  isValid: boolean;
  hasWarning?: boolean;
  triggerConditions: TriggerCondition[];
  approvers: Approver[];
  decisionRule: DecisionRule;
  delegationEnabled?: boolean;
  delegationUsers: DelegationUser[];
  sla: SLAConfig;
  metadata?: NodeMetadata;
  apiApprovalFlow?: any;
}

export interface WorkflowConfig {
  id: string;
  name: string;
  version: string;
  status: WorkflowStatus;
  lastEditedAgo: string;
  nodes: WorkflowNode[];
  apiWorkflow?: any;
}

export function createEmptyNode(type: NodeType, index: number): WorkflowNode {
  const id = `node-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const labels: Record<NodeType, string> = {
    AUTO_APPROVE: 'Auto-approve Gate',
    PARALLEL: 'Parallel Review Gate',
    HIERARCHY: 'Hierarchy Chain Gate',
  };
  return {
    id,
    type,
    label: labels[type],
    isValid: false,
    triggerConditions: [
      { id: `tc-${Date.now()}`, field: '', operator: '>', value: '', currency: 'USD' },
    ],
    approvers: [],
    decisionRule: { type: 'ANY_ONE', description: 'Any one approver can approve' },
    delegationEnabled: type === 'PARALLEL',
    delegationUsers: [],
    sla: {
      reminderHours: 24,
      maxEscalations: 2,
      onTimeout: 'ESCALATE',
      escalationChain: [],
    },
    metadata: {
      id,
      createdAt: new Date().toISOString().split('T')[0],
      createdBy: 'current-user',
      lastEdited: 'just now',
      editCount: 0,
      usedBy: 'This workflow',
    },
  };
}
